import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Eye, EyeOff, ExternalLink, Loader2, Check, Info, ChevronDown, ChevronRight } from 'lucide-react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { StorefrontSettingsGuard } from './StorefrontSettingsGuard'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'

// --- Platform Icons ---

function MetaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" className={className}>
      <defs>
        <linearGradient id="meta-gr" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0064E0" />
          <stop offset="50%" stopColor="#0064E0" />
          <stop offset="100%" stopColor="#0073FA" />
        </linearGradient>
      </defs>
      <path fill="url(#meta-gr)" d="M115.4 128c-29.4 0-56 18.5-77.2 49.8C17 210 4 251 4 288c0 16.2 1.6 31.3 4.8 45.1 5.1 21.7 14.8 39.4 28.6 49.8 10.3 7.7 22.4 11.1 35.7 11.1 19.8 0 38.3-5 55.5-14.6 16.3-9.1 32-22.6 47.7-40.8L256 248l79.7 90.6c15.7 18.2 31.4 31.7 47.7 40.8 17.2 9.6 35.7 14.6 55.5 14.6 13.3 0 25.4-3.4 35.7-11.1 13.8-10.4 23.5-28.1 28.6-49.8 3.2-13.8 4.8-28.9 4.8-45.1 0-37-13-78-34.2-110.2C452.6 146.5 426 128 396.6 128c-23.9 0-47.8 8.3-71.8 24.9-18.6 12.9-35.9 30.2-51.9 51.8L256 226.6l-16.9-21.9c-16-21.6-33.3-38.9-51.9-51.8C163.2 136.3 139.3 128 115.4 128z" />
    </svg>
  )
}

function GoogleAdsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill="#FBBC04" d="M1.24 15.06L8.12 3.24c1.32-2.28 4.24-3.06 6.52-1.74s3.06 4.24 1.74 6.52L9.5 20.2c-1.32 2.28-4.24 3.06-6.52 1.74A4.75 4.75 0 0 1 1.24 15.06z" />
      <path fill="#4285F4" d="M15.88 20.2L8.98 8.38c-1.32-2.28-.54-5.2 1.74-6.52s5.2-.54 6.52 1.74l6.88 11.82c1.32 2.28.54 5.2-1.74 6.52s-5.2.54-6.52-1.74z" />
      <circle fill="#34A853" cx="5.74" cy="19.45" r="3.74" />
    </svg>
  )
}

function GA4Icon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill="#F9AB00" d="M22.84 2.998v17.958a3.017 3.017 0 0 1-3.022 3.022 3.017 3.017 0 0 1-3.022-3.022V2.998A3.003 3.003 0 0 1 19.818 0a3.003 3.003 0 0 1 3.022 2.998z" />
      <path fill="#E37400" d="M14.388 11.985v8.971a3.017 3.017 0 0 1-3.022 3.022 3.017 3.017 0 0 1-3.022-3.022v-8.971a3.003 3.003 0 0 1 3.022-2.998 3.003 3.003 0 0 1 3.022 2.998z" />
      <circle fill="#E37400" cx="3.044" cy="20.978" r="3.022" />
    </svg>
  )
}

// --- Tracking Event Cards (educational) ---

const TRACKING_EVENTS = [
  {
    name: 'Page View',
    desc: 'Tracked automatically when someone visits your store',
    value: 'Build audiences of people who browsed — then show them your ads on Facebook and Instagram',
    platforms: ['Meta', 'GA4'] as const,
    auto: true,
  },
  {
    name: 'Add to Cart',
    desc: 'Tracked when a customer adds a product to their cart',
    value: 'Retarget shoppers who were interested but didn\'t buy — the highest-intent audience for your ads',
    platforms: ['Meta', 'GA4'] as const,
  },
  {
    name: 'Purchase',
    desc: 'Tracked when a customer completes checkout and places an order',
    value: 'See exactly which ads led to real sales — know your return on ad spend (ROAS)',
    platforms: ['Meta', 'Google Ads', 'GA4'] as const,
    serverSide: true,
  },
  {
    name: 'Lead',
    desc: 'Tracked when a new customer registers or signs up',
    value: 'Find more customers like your existing ones — ad platforms use this to optimize targeting',
    platforms: ['Meta', 'GA4'] as const,
  },
]

const PLATFORM_PILL: Record<string, string> = {
  Meta: 'bg-blue-50 text-blue-700',
  'Google Ads': 'bg-amber-50 text-amber-700',
  GA4: 'bg-orange-50 text-orange-700',
}

// --- Status Logic ---

type StatusType = 'not_configured' | 'configured' | 'enabled'

const STATUS_CONFIG: Record<StatusType, { label: string; className: string }> = {
  not_configured: {
    label: 'Not configured',
    className: 'bg-gray-100 text-gray-600 border-0',
  },
  configured: {
    label: 'Configured',
    className: 'bg-amber-50 text-amber-700 border-0',
  },
  enabled: {
    label: 'Enabled',
    className: 'bg-green-50 text-green-700 border-0',
  },
}

function getMetaStatus(pixelId: string, enabled: boolean): StatusType {
  if (!pixelId) return 'not_configured'
  if (!enabled) return 'configured'
  return 'enabled'
}

function getGoogleAdsStatus(customerId: string, enabled: boolean): StatusType {
  if (!customerId) return 'not_configured'
  if (!enabled) return 'configured'
  return 'enabled'
}

function getGA4Status(measurementId: string, enabled: boolean): StatusType {
  if (!measurementId) return 'not_configured'
  if (!enabled) return 'configured'
  return 'enabled'
}

// --- Page ---

export function AdsTrackingPage() {
  const navigate = useNavigate()
  const { settings, isLoading, updateAsync, isUpdating } =
    useStorefrontTenantSettings()

  const [enableAdsTracking, setEnableAdsTracking] = useState(false)
  const [metaPixelId, setMetaPixelId] = useState('')
  const [metaCapiAccessToken, setMetaCapiAccessToken] = useState('')
  const [metaTestEventCode, setMetaTestEventCode] = useState('')
  const [googleAdsCustomerId, setGoogleAdsCustomerId] = useState('')
  const [googleAdsConversionActionId, setGoogleAdsConversionActionId] = useState('')
  const [googleAdsConversionLabel, setGoogleAdsConversionLabel] = useState('')
  const [ga4MeasurementId, setGa4MeasurementId] = useState('')
  const [ga4ApiSecret, setGa4ApiSecret] = useState('')
  const [utmCookieDays, setUtmCookieDays] = useState('90')

  const [showCapiToken, setShowCapiToken] = useState(false)
  const [showGa4Secret, setShowGa4Secret] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showEventsModal, setShowEventsModal] = useState(false)
  const [showMetaAdvanced, setShowMetaAdvanced] = useState(false)
  const [showGadsAdvanced, setShowGadsAdvanced] = useState(false)
  const [showGa4Advanced, setShowGa4Advanced] = useState(false)

  useEffect(() => {
    if (settings) {
      setEnableAdsTracking(!!settings.enable_ads_tracking)
      setMetaPixelId(settings.meta_pixel_id ?? '')
      setMetaCapiAccessToken(settings.meta_capi_access_token ?? '')
      setMetaTestEventCode(settings.meta_test_event_code ?? '')
      setGoogleAdsCustomerId(settings.google_ads_customer_id ?? '')
      setGoogleAdsConversionActionId(settings.google_ads_conversion_action_id ?? '')
      setGoogleAdsConversionLabel(settings.google_ads_conversion_label ?? '')
      setGa4MeasurementId(settings.ga4_measurement_id ?? '')
      setGa4ApiSecret(settings.ga4_api_secret ?? '')
      setUtmCookieDays(String(settings.utm_cookie_days ?? 90))
    }
  }, [settings])

  const handleSave = useCallback(async () => {
    try {
      await updateAsync({
        enable_ads_tracking: enableAdsTracking ? 1 : 0,
        meta_pixel_id: metaPixelId.trim(),
        meta_capi_access_token: metaCapiAccessToken.trim(),
        meta_test_event_code: metaTestEventCode.trim(),
        google_ads_customer_id: googleAdsCustomerId.trim(),
        google_ads_conversion_action_id: googleAdsConversionActionId.trim(),
        google_ads_conversion_label: googleAdsConversionLabel.trim(),
        ga4_measurement_id: ga4MeasurementId.trim(),
        ga4_api_secret: ga4ApiSecret.trim(),
        utm_cookie_days: parseInt(utmCookieDays, 10) || 90,
      })
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 1500)
      toast.success('Ads tracking settings saved')
    } catch {
      toast.error('Failed to save ads tracking settings')
    }
  }, [
    enableAdsTracking, metaPixelId, metaCapiAccessToken, metaTestEventCode,
    googleAdsCustomerId, googleAdsConversionActionId, googleAdsConversionLabel,
    ga4MeasurementId, ga4ApiSecret, utmCookieDays, updateAsync,
  ])

  const hasChanges =
    settings &&
    (enableAdsTracking !== !!settings.enable_ads_tracking ||
      metaPixelId.trim() !== (settings.meta_pixel_id ?? '') ||
      metaCapiAccessToken.trim() !== (settings.meta_capi_access_token ?? '') ||
      metaTestEventCode.trim() !== (settings.meta_test_event_code ?? '') ||
      googleAdsCustomerId.trim() !== (settings.google_ads_customer_id ?? '') ||
      googleAdsConversionActionId.trim() !== (settings.google_ads_conversion_action_id ?? '') ||
      googleAdsConversionLabel.trim() !== (settings.google_ads_conversion_label ?? '') ||
      ga4MeasurementId.trim() !== (settings.ga4_measurement_id ?? '') ||
      ga4ApiSecret.trim() !== (settings.ga4_api_secret ?? '') ||
      String(parseInt(utmCookieDays, 10) || 90) !== String(settings.utm_cookie_days ?? 90))

  const metaStatus = getMetaStatus(metaPixelId, enableAdsTracking)
  const googleAdsStatus = getGoogleAdsStatus(googleAdsCustomerId, enableAdsTracking)
  const ga4Status = getGA4Status(ga4MeasurementId, enableAdsTracking)

  return (
    <StorefrontSettingsGuard>
      <PageContainer>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => navigate('/storefront/settings')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-2xl font-semibold">Ads Tracking</h2>
              <p className="text-sm text-muted-foreground">
                Meta Pixel, Google Ads, and GA4 tracking configuration
              </p>
            </div>
          </div>

          {isLoading ? (
            <Card className="rounded-2xl border-0">
              <CardContent className="p-6 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Master Toggle */}
              <Card className="rounded-2xl border-0">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="ads-master-toggle" className="text-sm font-medium">
                        Enable Ads Tracking
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Inject tracking scripts into your storefront for conversion tracking
                      </p>
                    </div>
                    <Switch
                      id="ads-master-toggle"
                      checked={enableAdsTracking}
                      onCheckedChange={setEnableAdsTracking}
                      disabled={isUpdating}
                      aria-label="Enable Ads Tracking"
                    />
                  </div>
                  <div className="flex gap-2">
                    {([
                      { label: 'Meta Pixel', status: metaStatus, icon: <MetaIcon className="w-3 h-3" /> },
                      { label: 'Google Ads', status: googleAdsStatus, icon: <GoogleAdsIcon className="w-3 h-3" /> },
                      { label: 'GA4', status: ga4Status, icon: <GA4Icon className="w-3 h-3" /> },
                    ] as const).map(({ label, status, icon }) => (
                      <div
                        key={label}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 text-xs font-medium text-muted-foreground"
                      >
                        {icon}
                        {label}
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          status === 'enabled' ? 'bg-green-500' :
                          status === 'configured' ? 'bg-amber-500' :
                          'bg-gray-300'
                        }`} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Platform cards — dim when tracking disabled */}
              <div className={!enableAdsTracking ? 'opacity-50 pointer-events-none' : ''}>

              {/* Meta (Facebook) */}
              <Card className="rounded-2xl border-0">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MetaIcon className="w-4 h-4 text-[#0081FB]" />
                      <span className="font-semibold">Meta Pixel</span>
                    </div>
                    <Badge className={STATUS_CONFIG[metaStatus].className}>
                      {STATUS_CONFIG[metaStatus].label}
                    </Badge>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="meta-pixel-id" className="text-sm">
                      Pixel ID
                    </Label>
                    <Input
                      id="meta-pixel-id"
                      placeholder="1234567890123456"
                      value={metaPixelId}
                      onChange={(e) => setMetaPixelId(e.target.value)}
                      disabled={isUpdating}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowMetaAdvanced(!showMetaAdvanced)}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    {showMetaAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    Advanced (CAPI token, test event code)
                  </button>

                  {showMetaAdvanced && (
                    <div className="space-y-4 rounded-lg bg-gray-50 p-4 border border-gray-100">
                      <div className="space-y-1.5">
                        <Label htmlFor="meta-capi-token" className="text-sm">
                          Conversions API Access Token
                        </Label>
                        <div className="relative">
                          <Input
                            id="meta-capi-token"
                            type={showCapiToken ? 'text' : 'password'}
                            placeholder="EAAxxxxxxx..."
                            value={metaCapiAccessToken}
                            onChange={(e) => setMetaCapiAccessToken(e.target.value)}
                            disabled={isUpdating}
                            className="pr-10 bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCapiToken(!showCapiToken)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showCapiToken ? 'Hide token' : 'Show token'}
                          >
                            {showCapiToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Server-side tracking for improved conversion accuracy
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="meta-test-code" className="text-sm">
                          Test Event Code
                        </Label>
                        <Input
                          id="meta-test-code"
                          placeholder="TEST12345"
                          value={metaTestEventCode}
                          onChange={(e) => setMetaTestEventCode(e.target.value)}
                          disabled={isUpdating}
                          className="bg-white"
                        />
                        <p className="text-xs text-muted-foreground">
                          Use during testing only. Remove before going live.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-4 mt-2">
                    <p className="text-xs font-medium text-muted-foreground mb-2">How to set up</p>
                    <ol className="space-y-1.5">
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">1</span>
                        <span>
                          Open{' '}
                          <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Meta Events Manager <ExternalLink className="w-2.5 h-2.5 inline" />
                          </a>
                        </span>
                      </li>
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">2</span>
                        <span>Select your Pixel from the left sidebar</span>
                      </li>
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">3</span>
                        <span>Copy the Pixel ID and paste above</span>
                      </li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* Google Ads */}
              <Card className="rounded-2xl border-0 mt-4">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GoogleAdsIcon className="w-4 h-4" />
                      <span className="font-semibold">Google Ads</span>
                    </div>
                    <Badge className={STATUS_CONFIG[googleAdsStatus].className}>
                      {STATUS_CONFIG[googleAdsStatus].label}
                    </Badge>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="gads-customer-id" className="text-sm">
                      Customer ID
                    </Label>
                    <Input
                      id="gads-customer-id"
                      placeholder="AW-1234567890"
                      value={googleAdsCustomerId}
                      onChange={(e) => setGoogleAdsCustomerId(e.target.value)}
                      disabled={isUpdating}
                    />
                    <p className="text-xs text-muted-foreground">
                      Your Google Ads account ID (AW-xxxxxxxxxx format)
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowGadsAdvanced(!showGadsAdvanced)}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    {showGadsAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    Advanced (conversion action, label)
                  </button>

                  {showGadsAdvanced && (
                    <div className="space-y-4 rounded-lg bg-gray-50 p-4 border border-gray-100">
                      <div className="space-y-1.5">
                        <Label htmlFor="gads-conversion-action" className="text-sm">
                          Conversion Action ID
                        </Label>
                        <Input
                          id="gads-conversion-action"
                          placeholder="7333339390"
                          value={googleAdsConversionActionId}
                          onChange={(e) => setGoogleAdsConversionActionId(e.target.value)}
                          disabled={isUpdating}
                          className="bg-white"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="gads-conversion-label" className="text-sm">
                          Conversion Label
                        </Label>
                        <Input
                          id="gads-conversion-label"
                          placeholder="AW-1234567890/AbCdEfGhIjKl"
                          value={googleAdsConversionLabel}
                          onChange={(e) => setGoogleAdsConversionLabel(e.target.value)}
                          disabled={isUpdating}
                          className="bg-white"
                        />
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-4 mt-2">
                    <p className="text-xs font-medium text-muted-foreground mb-2">How to set up</p>
                    <ol className="space-y-1.5">
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">1</span>
                        <span>
                          Open{' '}
                          <a href="https://ads.google.com/aw/conversions" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Google Ads Console <ExternalLink className="w-2.5 h-2.5 inline" />
                          </a>
                          {' '}and go to Tools &rarr; Conversions
                        </span>
                      </li>
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">2</span>
                        <span>Find your Customer ID in the top right corner (AW-xxx format)</span>
                      </li>
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">3</span>
                        <span>Copy and paste above</span>
                      </li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* GA4 */}
              <Card className="rounded-2xl border-0 mt-4">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GA4Icon className="w-4 h-4 text-[#E37400]" />
                      <span className="font-semibold">Google Analytics 4</span>
                    </div>
                    <Badge className={STATUS_CONFIG[ga4Status].className}>
                      {STATUS_CONFIG[ga4Status].label}
                    </Badge>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="ga4-measurement-id" className="text-sm">
                      Measurement ID
                    </Label>
                    <Input
                      id="ga4-measurement-id"
                      placeholder="G-XXXXXXXXXX"
                      value={ga4MeasurementId}
                      onChange={(e) => setGa4MeasurementId(e.target.value)}
                      disabled={isUpdating}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowGa4Advanced(!showGa4Advanced)}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    {showGa4Advanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    Advanced (API secret)
                  </button>

                  {showGa4Advanced && (
                    <div className="space-y-4 rounded-lg bg-gray-50 p-4 border border-gray-100">
                      <div className="space-y-1.5">
                        <Label htmlFor="ga4-api-secret" className="text-sm">
                          API Secret
                        </Label>
                        <div className="relative">
                          <Input
                            id="ga4-api-secret"
                            type={showGa4Secret ? 'text' : 'password'}
                            placeholder="xxxxxxxxxxxxxxxx"
                            value={ga4ApiSecret}
                            onChange={(e) => setGa4ApiSecret(e.target.value)}
                            disabled={isUpdating}
                            className="pr-10 bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowGa4Secret(!showGa4Secret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showGa4Secret ? 'Hide secret' : 'Show secret'}
                          >
                            {showGa4Secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          For server-side Measurement Protocol tracking
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-4 mt-2">
                    <p className="text-xs font-medium text-muted-foreground mb-2">How to set up</p>
                    <ol className="space-y-1.5">
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">1</span>
                        <span>
                          Open{' '}
                          <a href="https://analytics.google.com/analytics/web/#/a/admin" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            GA4 Admin Console <ExternalLink className="w-2.5 h-2.5 inline" />
                          </a>
                        </span>
                      </li>
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">2</span>
                        <span>Go to Data Streams and select your stream</span>
                      </li>
                      <li className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">3</span>
                        <span>Copy the Measurement ID (G-xxx format)</span>
                      </li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* What Gets Tracked — trigger link */}
              <button
                type="button"
                onClick={() => setShowEventsModal(true)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-1 mt-4"
              >
                <Info className="w-3.5 h-3.5" />
                <span>What gets tracked?</span>
              </button>

              {/* What Gets Tracked — Modal */}
              <Dialog open={showEventsModal} onOpenChange={setShowEventsModal}>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>What Gets Tracked</DialogTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      These events are sent to your ad platforms automatically — no code needed
                    </p>
                  </DialogHeader>

                  <div className="space-y-4 mt-2">
                    {/* How it works — 3-step visual */}
                    <div className="rounded-lg bg-gray-50 p-4">
                      <div className="flex items-center justify-between gap-1 text-center">
                        <div className="flex-1 space-y-1">
                          <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center mx-auto text-xs font-semibold text-gray-600">1</div>
                          <p className="text-[11px] text-muted-foreground leading-tight">Customer<br />visits your store</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                        <div className="flex-1 space-y-1">
                          <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center mx-auto text-xs font-semibold text-gray-600">2</div>
                          <p className="text-[11px] text-muted-foreground leading-tight">Events sent to<br />your ad platforms</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                        <div className="flex-1 space-y-1">
                          <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center mx-auto text-xs font-semibold text-gray-600">3</div>
                          <p className="text-[11px] text-muted-foreground leading-tight">Ads optimize<br />automatically</p>
                        </div>
                      </div>
                    </div>

                    {/* Event cards */}
                    <div className="space-y-3">
                      {TRACKING_EVENTS.map((event) => (
                        <div key={event.name} className="rounded-lg border border-gray-100 p-4 space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-medium text-[#111]">{event.name}</p>
                                {event.auto && (
                                  <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                                    Automatic
                                  </span>
                                )}
                                {event.serverSide && (
                                  <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">
                                    Server Verified
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{event.desc}</p>
                            </div>
                            <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                              {event.platforms.map((p) => (
                                <span
                                  key={p}
                                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PLATFORM_PILL[p]}`}
                                >
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground/80 italic">{event.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* UTM Tracking */}
              <Card className="rounded-2xl border-0 mt-4">
                <CardContent className="p-6 space-y-4">
                  <span className="font-semibold">UTM Tracking</span>

                  <div className="space-y-1.5">
                    <Label htmlFor="utm-cookie-days" className="text-sm">
                      Cookie Duration (Days)
                    </Label>
                    <Input
                      id="utm-cookie-days"
                      type="number"
                      min="1"
                      max="365"
                      placeholder="90"
                      value={utmCookieDays}
                      onChange={(e) => setUtmCookieDays(e.target.value)}
                      disabled={isUpdating}
                    />
                    <p className="text-xs text-muted-foreground">
                      How long UTM parameters and click IDs are stored in cookies (default: 90 days)
                    </p>
                  </div>
                </CardContent>
              </Card>

              </div>{/* End dim wrapper */}

              {/* Save */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={isUpdating || !hasChanges}
                  className="min-w-[100px]"
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                      Saving...
                    </>
                  ) : saveSuccess ? (
                    <>
                      <Check className="w-4 h-4 mr-1.5 text-green-600" />
                      Saved
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </PageContainer>
    </StorefrontSettingsGuard>
  )
}
